import { useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from '../../../../lib/store';
import {
  getDefaultVisibility,
  MODIFIER_VISIBILITY_RULES,
} from '../../../workspace-common/config/attributeRules';
import { ATTR_EXPERIENCED_NAME, ATTR_PRESENT_NAME } from '../../../workspace-common/constants';
import type { Annotation, Attribute, Span } from '../../../workspace-common/types/responseTypes';
import type { FormAttributeValue } from '../../../workspace-common/types/types';
import {
  buildAttributeValues,
  getInitializeFormAttributes,
} from '../../../workspace-common/utils/annotationCore';
import { useAnnotationFormCore } from '../../../workspace-common/utils/useAnnotationFormCore';
import { useGetAnnotationWorkspaceQuery } from '../../api/annotationApiSlice';
import {
  useAddAnnotationSpansMutation,
  useCreateAnnotationMutation,
  useUpdateAnnotationAttributesMutation,
} from '../../api/annotationApiSlice';
import { ANNOTATION_TAG } from '../../constants';
import {
  removeFromConceptAnnotationIdsInEditMode,
  setAddEvidenceModeForConceptId,
  setHighlightedTextSpans,
  clearPendingSpansForAnnotation,
  clearHighlightedTextSpans,
  clearAddEvidenceModeForConceptId,
  setCreateAnnotationError,
  setEditAnnotationError,
  clearEditAnnotationError,
  clearCreateAnnotationError,
  clearEvidenceIdsToDelete,
  addToDirtyAnnotationKeys,
  removeFromDirtyAnnotationKeys,
} from '../../state/annotationSlice';
import {
  selectCreateAnnotationError,
  selectEditAnnotationError,
  selectPendingSpansForAnnotation,
  selectEvidenceIdsToDelete,
} from '../../state/selector';
import { selectAddEvidenceModeForConceptId } from '../../state/selector';

interface UseInProgressAnnotationCardProps {
  annotation: Annotation | null;
  conceptId: string;
  editingAnnotationId?: string;
}

export interface UseInProgressAnnotationCardResult {
  formAttributes: FormAttributeValue[];
  isAttributeVisible: (name: string) => boolean;
  getAttributeRule: (name: string) => Attribute | undefined;
  handleChangeAttribute: (name: string, updates: Partial<FormAttributeValue>) => void;
  isPresent: boolean;
  addEvidenceModeForConceptId: string | null;
  evidenceRequired: boolean;
  enableSaveButton: boolean;
  handleSaveAnnotation: () => Promise<void>;
  startAnnotationMode: (annotation: Annotation | null) => void;
  currentAnnotationId: string;
  isPresentNull: boolean;
  isSaveAttributesLoading: boolean;
  isSaveSpansLoading: boolean;
  editAnnotationError: string | null;
  closeEditAnnotationError: () => void;
  createAnnotationError: string | null;
  closeCreateAnnotationError: () => void;
  handleCancelEdit: () => void;
  handleCancelNewAnnotation: () => void;
  hasAnyAttributeValue: boolean;
}

export const useInProgressAnnotationCard = ({
  annotation,
  conceptId,
  editingAnnotationId,
}: UseInProgressAnnotationCardProps): UseInProgressAnnotationCardResult => {
  const { taskId } = useParams();
  const { data: annotationWorkspaceData } = useGetAnnotationWorkspaceQuery({
    taskId: taskId as string,
  });
  const dispatch = useAppDispatch();

  const concept = annotationWorkspaceData?.data?.concepts.find((c) => c.conceptId === conceptId);
  const attributes = concept?.attributes;

  const evidenceIdsToDeleteMap = useAppSelector(selectEvidenceIdsToDelete);
  const evidenceIdsToDelete = evidenceIdsToDeleteMap[annotation?.annotationId || ''] || [];

  const {
    formAttributes,
    setFormAttributes,
    getAttributeRule,
    isAttributeVisible,
    handleChangeAttribute,
    isPresent,
    isPresentNull,
    getTemporaryAnnotationId,
    currentAnnotationId,
    pendingSpans,
    evidenceRequired,
    addEvidenceModeForConceptId,
    startAnnotationMode,
    enableSaveButton,
  } = useAnnotationFormCore({
    annotation,
    conceptId,
    editingAnnotationId,
    attributes,
    selectors: { selectAddEvidenceModeForConceptId, selectPendingSpansForAnnotation },
    actions: {
      clearPendingSpansForAnnotation,
      clearHighlightedTextSpans,
      clearAddEvidenceModeForConceptId,
      setAddEvidenceModeForConceptId,
      setHighlightedTextSpans,
    },
    evidenceIdsToDelete,
  });

  // Track dirty state per annotation key (conceptId + annotationId)
  // An annotation is dirty when the user has set any attribute value (regardless of completeness)
  const hasAnyAttributeValue = formAttributes.some((attr) => attr.value !== undefined);
  useEffect(() => {
    const annotationKey = conceptId + currentAnnotationId;
    if (hasAnyAttributeValue) {
      dispatch(addToDirtyAnnotationKeys(annotationKey));
    } else {
      dispatch(removeFromDirtyAnnotationKeys(annotationKey));
    }
    return () => {
      dispatch(removeFromDirtyAnnotationKeys(annotationKey));
    };
  }, [hasAnyAttributeValue, conceptId, currentAnnotationId]);

  // Reset hidden attribute values when visibility-affecting values change
  const presentValue = formAttributes.find((m) => m.name === ATTR_PRESENT_NAME)?.value;
  const experiencedValue = formAttributes.find((m) => m.name === ATTR_EXPERIENCED_NAME)?.value;

  useEffect(() => {
    if (!attributes) return;

    setFormAttributes((prev) => {
      const currentPresent = prev.find((m) => m.name === ATTR_PRESENT_NAME)?.value;
      const currentExperienced = prev.find((m) => m.name === ATTR_EXPERIENCED_NAME)?.value;
      if (currentPresent === presentValue && currentExperienced === experiencedValue) return prev;

      const getPrevValue = (name: string) => prev.find((m) => m.name === name)?.value;
      const checkVisibility = (name: string) => {
        const explicitRule = MODIFIER_VISIBILITY_RULES.find((rule) => rule.attributeName === name);
        if (explicitRule) return explicitRule.isVisible(getPrevValue);
        return getDefaultVisibility(name, getPrevValue);
      };

      return attributes.map((attribute) => {
        const existing = prev.find((m) => m.name === attribute.attributeName);
        const base = existing || {
          attributeId: attribute.attributeId,
          name: attribute.attributeName,
          value: undefined,
          supportedIndirectly: false,
          unknown: false,
        };
        if (!checkVisibility(attribute.attributeName)) {
          return {
            ...base,
            value: undefined,
            supportedIndirectly: false,
            unknown: false,
            attributeId: attribute.attributeId,
          };
        }
        return base;
      });
    });
  }, [presentValue, experiencedValue, attributes]);

  const [addAnnotationSpans, { isLoading: isAddAnnotationSpansLoading }] =
    useAddAnnotationSpansMutation();
  const [createAnnotation, { isLoading: isCreateAnnotationLoading }] =
    useCreateAnnotationMutation();
  const [updateAnnotationAttributes, { isLoading: isUpdateAnnotationAttributesLoading }] =
    useUpdateAnnotationAttributesMutation();

  const isSavingRef = useRef(false);

  const handleSaveAnnotation = async () => {
    if (isSavingRef.current) return;
    isSavingRef.current = true;
    try {
      if (!taskId) return;

      const attributeValues = buildAttributeValues(
        attributes || [],
        formAttributes,
        isAttributeVisible
      );

      if (!annotation?.annotationId) {
        try {
          const spansToSave = pendingSpans.map((span: Span) => ({
            noteId: span.noteId,
            offsetStart: span.offsetStart,
            offsetEnd: span.offsetEnd,
            text: span.text,
          }));
          await createAnnotation({
            taskId,
            conceptId,
            attributes: attributeValues,
            spans: spansToSave,
          }).unwrap();
          dispatch(clearPendingSpansForAnnotation(getTemporaryAnnotationId()));
          dispatch(clearHighlightedTextSpans());
          dispatch(clearAddEvidenceModeForConceptId());
          dispatch(
            removeFromConceptAnnotationIdsInEditMode(conceptId + getTemporaryAnnotationId())
          );
          // Create errors are keyed by conceptId (shared across new-annotation
          // cards for the same concept), so clear this concept's error on
          // success — otherwise a freshly opened empty card inherits it.
          dispatch(setCreateAnnotationError({ conceptId, error: null }));
        } catch (err: any) {
          const message =
            err?.data?.detail ||
            err?.data?.message ||
            err?.data?.errorMessage ||
            err?.message ||
            'Failed to save annotation';
          dispatch(setCreateAnnotationError({ conceptId: conceptId, error: message }));
        }
        return;
      }

      try {
        const attributesChanged = attributeValues.some((av, i) => {
          const orig = (annotation.attributes || [])[i];
          return (
            !orig ||
            av.value !== orig.value ||
            av.supportedIndirectly !== orig.supportedIndirectly ||
            av.unknown !== orig.unknown
          );
        });

        const spansToSave =
          pendingSpans.length > 0
            ? pendingSpans.map((span) => ({
                noteId: span.noteId,
                offsetStart: span.offsetStart,
                offsetEnd: span.offsetEnd,
                text: span.text,
              }))
            : undefined;

        const savedSpanEvidenceIdsToDelete =
          !isPresent && annotation.spans
            ? annotation.spans
                .map((s) => s.evidenceId)
                .filter((id): id is string => !!id && !evidenceIdsToDelete.includes(id))
            : [];

        const allEvidenceIdsToDelete = [...evidenceIdsToDelete, ...savedSpanEvidenceIdsToDelete];
        const evidenceIdsToDeletePayload =
          allEvidenceIdsToDelete.length > 0 ? allEvidenceIdsToDelete : undefined;

        if (attributesChanged || evidenceIdsToDeletePayload) {
          try {
            await updateAnnotationAttributes({
              annotationId: annotation.annotationId,
              attributes: attributeValues,
              spans: spansToSave,
              evidenceIdsToDelete: evidenceIdsToDeletePayload,
            }).unwrap();
            if (spansToSave) dispatch(clearPendingSpansForAnnotation(annotation.annotationId));
            dispatch(clearEvidenceIdsToDelete({ annotationId: annotation.annotationId }));
            dispatch(clearHighlightedTextSpans());
            dispatch(clearAddEvidenceModeForConceptId());
            dispatch(removeFromConceptAnnotationIdsInEditMode(conceptId + annotation.annotationId));
          } catch (err: any) {
            const message =
              err?.data?.detail ||
              err?.data?.message ||
              err?.data?.errorMessage ||
              err?.message ||
              'Failed to update annotation';
            dispatch(
              setEditAnnotationError({ annotationId: annotation.annotationId, error: message })
            );
          }
        } else if (spansToSave) {
          await addAnnotationSpans({
            annotationId: annotation.annotationId,
            spans: spansToSave,
          }).unwrap();
          dispatch(clearPendingSpansForAnnotation(annotation.annotationId));
          dispatch(clearHighlightedTextSpans());
          dispatch(clearAddEvidenceModeForConceptId());
          dispatch(removeFromConceptAnnotationIdsInEditMode(conceptId + annotation.annotationId));
        }
      } catch (error) {
        console.error('Failed to save annotation:', error);
      }
    } finally {
      isSavingRef.current = false;
    }
  };

  const editAnnotationErrorForId = useAppSelector(
    selectEditAnnotationError(annotation?.annotationId ?? '')
  );
  const editAnnotationError = annotation?.annotationId ? editAnnotationErrorForId : null;

  const closeEditAnnotationError = () => dispatch(clearEditAnnotationError());

  const createAnnotationError = useAppSelector(selectCreateAnnotationError(conceptId));
  const closeCreateAnnotationError = () => dispatch(clearCreateAnnotationError());

  const handleCancelEdit = () => {
    if (annotation) {
      dispatch(clearPendingSpansForAnnotation(annotation.annotationId));
      dispatch(clearEvidenceIdsToDelete({ annotationId: annotation.annotationId }));
      dispatch(clearAddEvidenceModeForConceptId());
      dispatch(clearHighlightedTextSpans());
      dispatch(removeFromConceptAnnotationIdsInEditMode(conceptId + annotation.annotationId));
    }
  };

  const handleCancelNewAnnotation = () => {
    setFormAttributes(getInitializeFormAttributes(attributes, null));
    dispatch(clearPendingSpansForAnnotation(currentAnnotationId));
    dispatch(clearHighlightedTextSpans());
    dispatch(clearAddEvidenceModeForConceptId());
    dispatch(setCreateAnnotationError({ conceptId, error: null }));
  };

  const enableSaveButtonFinal =
    enableSaveButton || (!!annotation && evidenceIdsToDelete.length > 0 && !evidenceRequired);

  return {
    formAttributes,
    isAttributeVisible,
    getAttributeRule,
    handleChangeAttribute,
    isPresent,
    addEvidenceModeForConceptId,
    evidenceRequired,
    enableSaveButton: enableSaveButtonFinal,
    handleSaveAnnotation,
    startAnnotationMode,
    currentAnnotationId,
    isPresentNull,
    isSaveAttributesLoading: isUpdateAnnotationAttributesLoading || isCreateAnnotationLoading,
    isSaveSpansLoading: isAddAnnotationSpansLoading,
    editAnnotationError,
    closeEditAnnotationError,
    createAnnotationError,
    closeCreateAnnotationError,
    handleCancelEdit,
    handleCancelNewAnnotation,
    hasAnyAttributeValue,
  };
};
